<template>
  <div class="mb-5 list-toolbar">
    <BaseButton variant="primary" :icon="ArrowDownTrayIcon" @click="exportData">
      Export Day's All Campaign Data
    </BaseButton>
    <BaseButton variant="primary" :icon="ArrowDownTrayIcon" @click="exportData">
      Export All Data by Date Range
    </BaseButton>
    <BaseButton variant="secondary" :icon="Cog6ToothIcon" @click="showCustomizeModal = true">
      Customize export template
    </BaseButton>
    <BaseButton
      variant="secondary"
      :icon="DocumentDuplicateIcon"
      @click="showTemplatesModal = true"
    >
      Load saved templates
    </BaseButton>
    <div class="list-toolbar__right">
      <BaseButton
        variant="secondary"
        :icon="FunnelIcon"
        @click="showFilterDrawer = !showFilterDrawer"
      >
        Filters
        <span
          v-if="activeFilterCount"
          class="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white"
        >
          {{ activeFilterCount }}
        </span>
      </BaseButton>
    </div>
  </div>

  <FilterDrawer
    :open="showFilterDrawer"
    title="Filter Exports"
    subtitle="Set the date range for campaign exports"
    @close="showFilterDrawer = false"
  >
    <div class="filter-fields export-filter-fields">
      <div>
        <label
          for="org-campaign-list-export-start"
          class="mb-1.5 block text-sm font-medium text-gray-700"
        >
          Start date
        </label>
        <FlatPickr
          id="org-campaign-list-export-start"
          name="org-campaign-list-export-start"
          class="input--text--small placeholder:text-gray-400 w-full"
          placeholder="Select date start"
          :value="selectedDate.date1"
          @input="handleDateChange('date1', $event.target.value)"
        />
      </div>
      <div>
        <label
          for="org-campaign-list-export-end"
          class="mb-1.5 block text-sm font-medium text-gray-700"
        >
          End date
        </label>
        <FlatPickr
          id="org-campaign-list-export-end"
          name="org-campaign-list-export-end"
          class="input--text--small placeholder:text-gray-400 w-full"
          placeholder="Select date end"
          :value="selectedDate.date2"
          @input="handleDateChange('date2', $event.target.value)"
        />
      </div>
      <div>
        <label
          for="org-campaign-list-export-campaign"
          class="mb-1.5 block text-sm font-medium text-gray-700"
        >
          Campaigns
        </label>
        <multiselect
          id="org-campaign-list-export-campaign"
          v-model="selectedExportCampaignFilter"
          :options="campaignMultiselectOptions"
          :multiple="true"
          :close-on-select="false"
          placeholder="All Campaigns"
          selectLabel="Apply"
          deselectLabel="Remove"
          :custom-label="campaignOptionLabel"
          track-by="id"
          clearable
          class="w-full"
        />
      </div>
      <div>
        <label
          for="org-campaign-list-export-fundraiser"
          class="mb-1.5 block text-sm font-medium text-gray-700"
        >
          Fundraisers
        </label>
        <multiselect
          id="org-campaign-list-export-fundraiser"
          v-model="selectedExportFundraiserFilter"
          :options="fundraiserFilterOptions"
          :multiple="true"
          :close-on-select="false"
          placeholder="All Fundraisers"
          selectLabel="Apply"
          deselectLabel="Remove"
          label="fullName"
          track-by="id"
          clearable
          class="w-full"
        />
      </div>
      <div>
        <label
          for="org-campaign-list-export-home-office"
          class="mb-1.5 block text-sm font-medium text-gray-700"
        >
          Home Offices
        </label>
        <multiselect
          id="org-campaign-list-export-home-office"
          v-model="selectedExportHomeOfficeFilter"
          :options="homeOfficeFilterOptions"
          :multiple="true"
          :close-on-select="false"
          placeholder="All Home Offices"
          selectLabel="Apply"
          deselectLabel="Remove"
          label="name"
          track-by="id"
          clearable
          class="w-full"
        />
      </div>
    </div>
    <template #footer>
      <div class="filter-drawer-footer">
        <BaseButton variant="ghost" @click="clearExportFilters">Clear</BaseButton>
        <BaseButton variant="primary" @click="showFilterDrawer = false">Done</BaseButton>
      </div>
    </template>
  </FilterDrawer>

  <div
    v-if="showCustomizeModal"
    class="fixed inset-0 overflow-hidden z-50"
    aria-labelledby="org-fundraiser-export-slide-over-title"
    role="dialog"
    aria-modal="true"
  >
    <div class="absolute inset-0 overflow-hidden">
      <div
        class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="showCustomizeModal = false"
        aria-hidden="true"
      ></div>
      <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div class="w-screen max-w-md">
          <div class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
            <div class="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
              <div class="flex items-start justify-between">
                <h2
                  class="text-lg font-medium text-gray-900"
                  id="org-fundraiser-export-slide-over-title"
                >
                  Customize Fundraiser Stats Export
                </h2>
                <div class="ml-3 h-7 flex items-center">
                  <button
                    type="button"
                    class="-m-2 p-2 text-gray-400 hover:text-gray-500"
                    @click="showCustomizeModal = false"
                  >
                    <span class="sr-only">Close panel</span>
                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div class="mt-8">
                <div class="flow-root">
                  <div class="flex flex-wrap gap-2">
                    <div
                      v-for="(field, key) in exportFields"
                      :key="key"
                      @click="toggleExportField(key)"
                      class="cursor-pointer"
                    >
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors"
                        :class="{
                          'bg-primary text-white': field.selected,
                          'bg-gray-100 text-gray-700 hover:bg-gray-200': !field.selected,
                        }"
                      >
                        {{ field.label }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-8">
                <label
                  for="org-fundraiser-customize-campaign"
                  class="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Campaign Filter
                </label>
                <multiselect
                  id="org-fundraiser-customize-campaign"
                  v-model="selectedCustomizeCampaignFilter"
                  :options="campaignMultiselectOptions"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="All Campaigns"
                  selectLabel="Apply"
                  deselectLabel="Remove"
                  :custom-label="campaignOptionLabel"
                  track-by="id"
                  clearable
                  class="w-full"
                />
              </div>

              <div class="mt-8">
                <label
                  for="org-fundraiser-customize-fundraiser"
                  class="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Fundraiser Filter
                </label>
                <multiselect
                  id="org-fundraiser-customize-fundraiser"
                  v-model="selectedCustomizeFundraiserFilter"
                  :options="fundraiserFilterOptions"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="All Fundraisers"
                  selectLabel="Apply"
                  deselectLabel="Remove"
                  label="fullName"
                  track-by="id"
                  clearable
                  class="w-full"
                />
              </div>

              <div class="flex flex-wrap gap-4 mt-8">
                <div class="flex items-center gap-2 flex-grow">
                  <FlatPickr
                    id="org-fundraiser-customize-start"
                    name="org-fundraiser-customize-start"
                    class="input--text--small placeholder:text-gray-400 date-input flex-grow"
                    placeholder="Select date start"
                    :value="selectedDate.date1"
                    @input="handleDateChange('date1', $event.target.value)"
                  />
                </div>
                <div class="flex items-center gap-2 flex-grow">
                  <FlatPickr
                    id="org-fundraiser-customize-end"
                    name="org-fundraiser-customize-end"
                    class="input--text--small placeholder:text-gray-400 date-input flex-grow"
                    placeholder="Select date end"
                    :value="selectedDate.date2"
                    @input="handleDateChange('date2', $event.target.value)"
                  />
                </div>
              </div>
            </div>

            <div
              class="border-t border-gray-200 py-6 px-4 sm:px-6 flex justify-between flex-wrap gap-2"
            >
              <div class="flex gap-2">
                <BaseButton variant="ghost" @click="clearExportSelection"
                  >Clear selection</BaseButton
                >
                <BaseButton
                  variant="secondary"
                  :disabled="selectedFieldsOrder.length === 0"
                  @click="showSaveTemplateModal = true"
                >
                  Save template
                </BaseButton>
              </div>
              <BaseButton
                variant="primary"
                :icon="ArrowDownTrayIcon"
                @click="exportCustomOrgFundraiserStats"
              >
                Export
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showSaveTemplateModal"
    class="fixed inset-0 overflow-hidden z-50"
    aria-labelledby="save-org-fundraiser-template-title"
    role="dialog"
    aria-modal="true"
  >
    <div class="absolute inset-0 overflow-hidden">
      <div
        class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="showSaveTemplateModal = false"
        aria-hidden="true"
      ></div>
      <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div class="w-screen max-w-md">
          <div class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
            <div class="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
              <div class="flex items-start justify-between">
                <h2
                  class="text-lg font-medium text-gray-900"
                  id="save-org-fundraiser-template-title"
                >
                  Save export template
                </h2>
                <div class="ml-3 h-7 flex items-center">
                  <button
                    type="button"
                    class="-m-2 p-2 text-gray-400 hover:text-gray-500"
                    @click="showSaveTemplateModal = false"
                  >
                    <span class="sr-only">Close panel</span>
                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div class="mt-8">
                <label
                  for="org-fundraiser-template-name"
                  class="block text-sm font-medium text-gray-700"
                >
                  Template name
                </label>
                <input
                  type="text"
                  id="org-fundraiser-template-name"
                  v-model="templateName"
                  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter template name"
                />
              </div>
            </div>

            <div class="border-t border-gray-200 py-6 px-4 sm:px-6 flex justify-between">
              <BaseButton variant="secondary" @click="showSaveTemplateModal = false">
                Cancel
              </BaseButton>
              <BaseButton
                variant="primary"
                :disabled="!templateName"
                @click="submitOrgStatsExportTemplate"
              >
                Save
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showTemplatesModal"
    class="fixed inset-0 overflow-hidden z-50"
    aria-labelledby="org-fundraiser-templates-modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div class="absolute inset-0 overflow-hidden">
      <div
        class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="showTemplatesModal = false"
        aria-hidden="true"
      ></div>
      <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div class="w-screen max-w-md">
          <div class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
            <div class="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
              <div class="flex items-start justify-between">
                <h2
                  class="text-lg font-medium text-gray-900"
                  id="org-fundraiser-templates-modal-title"
                >
                  Saved templates
                </h2>
                <div class="ml-3 h-7 flex items-center">
                  <button
                    type="button"
                    class="-m-2 p-2 text-gray-400 hover:text-gray-500"
                    @click="showTemplatesModal = false"
                  >
                    <span class="sr-only">Close panel</span>
                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div class="mt-8">
                <div v-if="loadingTemplates" class="text-center">
                  <Spinner />
                </div>
                <ul v-else class="divide-y divide-gray-200">
                  <li
                    v-for="template in savedOrgFundraiserStatsTemplates"
                    :key="template.id"
                    class="py-4 flex justify-between items-center gap-2"
                  >
                    <span class="text-sm font-medium text-gray-900 flex-grow">{{
                      template.name
                    }}</span>
                    <div class="flex gap-2 shrink-0">
                      <BaseButton
                        variant="secondary"
                        @click="applyOrgFundraiserStatsTemplate(template)"
                      >
                        Apply
                      </BaseButton>
                      <BaseButton
                        variant="danger"
                        @click="removeOrgStatsExportTemplate(template.id)"
                      >
                        Remove
                      </BaseButton>
                    </div>
                  </li>
                  <li
                    v-if="!savedOrgFundraiserStatsTemplates.length"
                    class="py-4 text-center text-gray-500"
                  >
                    No saved templates for this org export
                  </li>
                </ul>
              </div>
            </div>

            <div class="border-t border-gray-200 py-6 px-4 sm:px-6">
              <BaseButton variant="secondary" block @click="showTemplatesModal = false">
                Close
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div>
    <!-- Canvassing Org Campaign List (mobile) -->
    <div class="md:hidden">
      <ul v-if="campaigns?.length" role="list" class="mt-2 space-y-3">
        <li
          v-for="(campaign, key) in campaigns"
          :key="key"
          class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-gray-900">
                {{ campaign.location ? campaign.name + ' ' + campaign.location : campaign.name }}
              </p>
              <p class="truncate text-xs text-gray-500">{{ campaign.npo.name }}</p>
            </div>
            <RouterLink
              :to="{ name: 'CampaignData', params: { id: campaign.id } }"
              class="btn--table shrink-0"
            >
              View
            </RouterLink>
          </div>
          <div class="mt-3 flex flex-col gap-1 text-sm text-gray-700">
            <div class="flex items-center gap-1.5">
              <CheckIcon v-if="campaign.collectingDonations" class="h-5 w-5 text-primary" />
              <XMarkIcon v-else class="h-5 w-5 text-secondary" />
              <span>Collecting Donations</span>
            </div>
            <div class="flex items-center gap-1.5">
              <CheckIcon v-if="campaign.active" class="h-5 w-5 text-primary" />
              <XMarkIcon v-else class="h-5 w-5 text-secondary" />
              <span>{{ campaign.active ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
        </li>
      </ul>
      <p
        v-else
        class="mt-2 rounded-xl bg-white p-6 text-center text-sm text-gray-500 ring-1 ring-gray-200"
      >
        No results found.
      </p>
    </div>
    <!-- Canvassing Org Campaign List (desktop) -->
    <div class="hidden md:block">
      <div class="mt-2 flex flex-col">
        <BaseTable :columns="campaignTableColumns" :rows="campaigns" empty-text="No results found.">
          <template #cell-name="{ row }">
            <span class="font-medium text-gray-900">
              {{ row.location ? row.name + ' ' + row.location : row.name }}
            </span>
          </template>
          <template #cell-npo="{ row }">{{ row.npo.name }}</template>
          <template #cell-collecting="{ row }">
            <CheckIcon v-if="row.collectingDonations" class="mx-auto h-5 w-5 text-primary" />
            <XMarkIcon v-else class="mx-auto h-5 w-5 text-secondary" />
          </template>
          <template #cell-active="{ row }">
            <CheckIcon v-if="row.active" class="mx-auto h-5 w-5 text-primary" />
            <XMarkIcon v-else class="mx-auto h-5 w-5 text-secondary" />
          </template>
          <template #cell-actions="{ row }">
            <BaseButton variant="primary" size="xs" @click="goToCampaign(row.id)">View</BaseButton>
          </template>
        </BaseTable>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CheckIcon,
  FlagIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  FunnelIcon,
} from '@heroicons/vue/24/outline'
import { computed, onMounted, reactive, ref, toRefs, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import Spinner from '@/components/Spinner'
import { UserApi } from '@/services/users'
import { WorkedHoursApi } from '../services/workingHours'
import { FUNDRAISER_STATS_BY_DAY_FILTER_TYPE } from '../services/workingHours/models'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

export default {
  name: 'CanvassingOrgCampaigns',
  components: {
    CheckIcon,
    XMarkIcon,
    FlatPickr,
    Spinner,
    Multiselect,
  },
  props: {
    campaigns: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const toast = useToast()
    const router = useRouter()
    const store = useStore()
    const { campaigns } = toRefs(props)
    const isGivarooStaff = computed(() => store.getters.isGivarooStaff)
    const isSuperCanvassingOrgAdmin = computed(() => store.getters.isSuperCanvassingOrgAdmin)

    const campaignTableColumns = [
      { key: 'name', label: 'Name', minWidth: '12rem' },
      { key: 'npo', label: 'NPO', minWidth: '10rem' },
      { key: 'collecting', label: 'Collecting Donations', align: 'center' },
      { key: 'active', label: 'Active', align: 'center' },
      { key: 'actions', label: 'Actions', align: 'right' },
    ]

    const getCanvassingOrg = (campaign) => {
      if (campaign && campaign.length > 0) {
        return campaign[0].canvassingOrg
      }
      return undefined
    }

    const canvassingOrgUuid = computed(() => {
      const org = getCanvassingOrg(campaigns.value)
      if (!org) return ''
      return typeof org === 'string' ? org : org.id
    })

    // Super admins must not send a single org — backend scopes to all M2M orgs.
    const shouldPassSingleCanvassingOrg = computed(
      () => !isGivarooStaff.value && !isSuperCanvassingOrgAdmin.value && !!canvassingOrgUuid.value,
    )

    const hasOrgContext = computed(
      () =>
        isGivarooStaff.value ||
        isSuperCanvassingOrgAdmin.value ||
        !!canvassingOrgUuid.value ||
        (store.getters.accessibleCanvassingOrgs || []).length > 0,
    )

    const selectedDate = reactive({
      date1: '',
      date2: '',
    })

    const showFilterDrawer = ref(false)

    const selectedExportCampaignFilter = ref([])
    const selectedExportFundraiserFilter = ref([])
    const selectedExportHomeOfficeFilter = ref([])
    const selectedCustomizeFundraiserFilter = ref([])

    const filterIndexes = reactive({
      fundraisersFilter: [],
      homeOfficesFilter: [],
    })
    const fundraiserFilterOptions = computed(() => filterIndexes.fundraisersFilter || [])
    const homeOfficeFilterOptions = computed(() =>
      [...(filterIndexes.homeOfficesFilter || [])].sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()),
      ),
    )

    const activeFilterCount = computed(
      () =>
        (selectedDate.date1 ? 1 : 0) +
        (selectedDate.date2 ? 1 : 0) +
        selectedExportCampaignFilter.value.length +
        selectedExportFundraiserFilter.value.length +
        selectedExportHomeOfficeFilter.value.length,
    )

    const clearExportFilters = () => {
      selectedDate.date1 = ''
      selectedDate.date2 = ''
      selectedExportCampaignFilter.value = []
      selectedExportFundraiserFilter.value = []
      selectedExportHomeOfficeFilter.value = []
    }

    const selectedCustomizeCampaignFilter = ref([])

    const campaignMultiselectOptions = computed(() => campaigns.value || [])

    const campaignOptionLabel = (c) => {
      if (!c) return ''
      return c.location ? `${c.name} ${c.location}` : c.name
    }

    const idsCsv = (selected) =>
      (selected || [])
        .map((item) => item.id)
        .filter(Boolean)
        .join(',')

    const customizeCampaignIdsCsv = () => idsCsv(selectedCustomizeCampaignFilter.value)
    const customizeFundraiserIdsCsv = () => idsCsv(selectedCustomizeFundraiserFilter.value)

    const buildDrawerExportFilters = () => {
      const filters = {}
      const campaign = idsCsv(selectedExportCampaignFilter.value)
      const fundraiser = idsCsv(selectedExportFundraiserFilter.value)
      const homeOffice = idsCsv(selectedExportHomeOfficeFilter.value)
      if (campaign) filters.campaign = campaign
      if (fundraiser) filters.fundraiser = fundraiser
      if (homeOffice) filters.home_office = homeOffice
      return filters
    }

    const loadExportFilterOptions = async () => {
      try {
        const payload = {}
        if (shouldPassSingleCanvassingOrg.value) {
          payload.canvassingOrg = canvassingOrgUuid.value
        }
        const response = await UserApi.csc.retrieveFilterIndexes(
          Object.keys(payload).length ? payload : undefined,
        )
        filterIndexes.fundraisersFilter = response.fundraisers || []
        filterIndexes.homeOfficesFilter = response.homeOffices || []
      } catch (error) {
        console.error('Failed to load export filter options:', error)
      }
    }

    onMounted(() => {
      loadExportFilterOptions()
    })

    const handleDateChange = (dateField, value) => {
      selectedDate[dateField] = value
    }

    const exportData = async () => {
      if (!hasOrgContext.value) {
        toast.error('No organization context for export.')
        return
      }
      if (selectedDate.date1 || selectedDate.date2) {
        if (!selectedDate.date1 || !selectedDate.date2) {
          let date = ''
          if (selectedDate.date1) {
            date = selectedDate.date1
          } else {
            date = selectedDate.date2
          }
          try {
            const payload = { date: date, ...buildDrawerExportFilters() }
            if (shouldPassSingleCanvassingOrg.value) {
              payload.canvassing_org = canvassingOrgUuid.value
            }
            await WorkedHoursApi.csc.customRetrieveAll(payload)
          } catch (error) {
            console.error('Error exporting range data:', error)
          }
        } else {
          let startDate = new Date(selectedDate.date1)
          let endDate = new Date(selectedDate.date2)
          if (startDate > endDate) {
            ;[startDate, endDate] = [endDate, startDate]
          }
          const formattedStartDate = startDate.toISOString().split('T')[0]
          const formattedEndDate = endDate.toISOString().split('T')[0]

          try {
            const payload = {
              start_date: formattedStartDate,
              end_date: formattedEndDate,
              ...buildDrawerExportFilters(),
            }
            if (shouldPassSingleCanvassingOrg.value) {
              payload.canvassing_org = canvassingOrgUuid.value
            }
            await WorkedHoursApi.csc.rangeRetrieveAll(payload)
          } catch (error) {
            console.error('Error exporting range data:', error)
          }
        }
      }
    }

    const showCustomizeModal = ref(false)
    const showSaveTemplateModal = ref(false)
    const showTemplatesModal = ref(false)
    const templateName = ref('')
    const loadingTemplates = ref(false)
    const savedOrgFundraiserStatsTemplates = ref([])

    const exportFields = ref({
      date: { label: 'Date', selected: false },
      fundraiserFullName: { label: 'Fundraiser Name', selected: false },
      campaignName: { label: 'Campaign Name', selected: false },
      location: { label: 'Campaign Location', selected: false },
      recurringDonations: { label: 'Recurring Donations', selected: false },
      annualDonations: { label: 'Annual Donations', selected: false },
      oneTimeDonations: { label: 'One-Time Donations', selected: false },
      workedHours: { label: 'Worked Hours', selected: false },
      canvassingOrg: { label: 'Canvassing Org', selected: false },
      avgRecurringDonationAmount: { label: 'Avg Recurring Donation Amount', selected: false },
      avgRecurringDonorAge: { label: 'Avg Recurring Donor Age', selected: false },
      recurringDonorAgeOver30: { label: 'Recurring Donor Age over 30', selected: false },
      oneTimeDonationsSum: { label: 'One-Time Donations Sum', selected: false },
      homeOffice: { label: 'Home Office', selected: false },
      employeeId: { label: 'Employee ID', selected: false },
      stopCount: { label: 'Stop Count', selected: false },
    })
    const selectedFieldsOrder = ref([])

    const toggleExportField = (fieldKey) => {
      exportFields.value[fieldKey].selected = !exportFields.value[fieldKey].selected
      if (exportFields.value[fieldKey].selected) {
        selectedFieldsOrder.value.push(fieldKey)
      } else {
        selectedFieldsOrder.value = selectedFieldsOrder.value.filter((key) => key !== fieldKey)
      }
    }

    const clearExportSelection = () => {
      Object.keys(exportFields.value).forEach((key) => {
        exportFields.value[key].selected = false
      })
      selectedFieldsOrder.value = []
      selectedCustomizeCampaignFilter.value = []
      selectedCustomizeFundraiserFilter.value = []
      selectedDate.date1 = ''
      selectedDate.date2 = ''
    }

    const exportCustomOrgFundraiserStats = async () => {
      if (!hasOrgContext.value) {
        toast.error('No organization context for export.')
        return
      }
      if (!selectedFieldsOrder.value.length) {
        toast.error('Select at least one column to export.')
        return
      }
      if (!selectedDate.date1 && !selectedDate.date2) {
        toast.error('Select a date or date range for export.')
        return
      }
      try {
        let formattedStartDate = ''
        let formattedEndDate = ''
        if (!selectedDate.date2) {
          let date = selectedDate.date1 || selectedDate.date2
          const d = new Date(date)
          formattedStartDate = d.toISOString().split('T')[0]
          formattedEndDate = formattedStartDate
        } else {
          let startDate = new Date(selectedDate.date1)
          let endDate = new Date(selectedDate.date2)
          if (startDate > endDate) {
            ;[startDate, endDate] = [endDate, startDate]
          }
          formattedStartDate = startDate.toISOString().split('T')[0]
          formattedEndDate = endDate.toISOString().split('T')[0]
        }
        const downloadPayload = {
          dateRangeStart: formattedStartDate,
          dateRangeEnd: formattedEndDate,
          fields: selectedFieldsOrder.value,
        }
        if (shouldPassSingleCanvassingOrg.value) {
          downloadPayload.canvassingOrg = canvassingOrgUuid.value
        }
        const campaignCsv = customizeCampaignIdsCsv()
        if (campaignCsv) {
          downloadPayload.campaign = campaignCsv
        }
        const fundraiserCsv = customizeFundraiserIdsCsv()
        if (fundraiserCsv) {
          downloadPayload.fundraiser = fundraiserCsv
        }
        await WorkedHoursApi.csc.downloadCustomTemplate(downloadPayload)
        showCustomizeModal.value = false
      } catch (error) {
        console.error('Error exporting fundraiser stats:', error)
        toast.error('Export failed.')
      }
    }

    const submitOrgStatsExportTemplate = async () => {
      if (!hasOrgContext.value) {
        toast.error('No organization context for template.')
        return
      }
      if (!selectedFieldsOrder.value.length) {
        toast.error('Select at least one column before saving a template.')
        return
      }
      try {
        let formattedStartDate = ''
        if (selectedDate.date1 || selectedDate.date2) {
          const startDate = new Date(selectedDate.date1 || selectedDate.date2)
          if (!Number.isNaN(startDate.getTime())) {
            formattedStartDate = startDate.toISOString().split('T')[0]
          }
        }
        let formattedEndDate = ''
        if (selectedDate.date2) {
          const end = new Date(selectedDate.date2)
          if (!Number.isNaN(end.getTime())) {
            formattedEndDate = end.toISOString().split('T')[0]
          }
        }

        const campaignCsvForSave = customizeCampaignIdsCsv()
        const fundraiserCsvForSave = customizeFundraiserIdsCsv()
        await WorkedHoursApi.csc.saveFundraiserStatsTemplate({
          name: templateName.value,
          filterType: FUNDRAISER_STATS_BY_DAY_FILTER_TYPE,
          fields: selectedFieldsOrder.value,
          ...(shouldPassSingleCanvassingOrg.value
            ? { canvassingOrg: canvassingOrgUuid.value }
            : {}),
          ...(campaignCsvForSave ? { campaign: campaignCsvForSave } : {}),
          ...(fundraiserCsvForSave ? { fundraiser: fundraiserCsvForSave } : {}),
          dateRangeStart: formattedStartDate,
          dateRangeEnd: formattedEndDate,
        })
        templateName.value = ''
        showSaveTemplateModal.value = false
        showCustomizeModal.value = false
        toast.success('Template saved successfully.')
      } catch (error) {
        console.error(error)
        toast.error('Failed to save template.')
      }
    }

    const fetchOrgFundraiserStatsTemplates = async () => {
      if (!hasOrgContext.value) {
        savedOrgFundraiserStatsTemplates.value = []
        return
      }
      try {
        loadingTemplates.value = true
        const response = await WorkedHoursApi.csc.listFilterTemplates()
        const orgId = canvassingOrgUuid.value
        const list = response.results || response || []
        savedOrgFundraiserStatsTemplates.value = Array.isArray(list)
          ? list.filter((t) => {
              if (t.type !== FUNDRAISER_STATS_BY_DAY_FILTER_TYPE) return false
              if (isGivarooStaff.value || isSuperCanvassingOrgAdmin.value) return true
              const d = t.template || {}
              const oid = d.canvassing_org || d.canvassingOrg
              return oid && String(oid) === String(orgId)
            })
          : []
      } catch (error) {
        console.error(error)
        toast.error('Failed to load templates.')
      } finally {
        loadingTemplates.value = false
      }
    }

    const removeOrgStatsExportTemplate = async (templateId) => {
      try {
        await WorkedHoursApi.csc.removeFundraiserStatsTemplate({ templateId })
        toast.success('Template removed.')
        await fetchOrgFundraiserStatsTemplates()
      } catch (error) {
        console.error(error)
        toast.error('Failed to remove template.')
      }
    }

    const applyOrgFundraiserStatsTemplate = (template) => {
      clearExportSelection()
      const data = template.template || {}
      const campStr = data.campaign ?? data.campaign_id
      if (campStr) {
        const ids = String(campStr)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        const opts = campaignMultiselectOptions.value
        selectedCustomizeCampaignFilter.value = opts.filter((c) => ids.includes(String(c.id)))
      } else {
        selectedCustomizeCampaignFilter.value = []
      }
      const frStr = data.fundraiser ?? data.fundraiser_id
      if (frStr) {
        const ids = String(frStr)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        selectedCustomizeFundraiserFilter.value = fundraiserFilterOptions.value.filter((f) =>
          ids.includes(String(f.id)),
        )
      } else {
        selectedCustomizeFundraiserFilter.value = []
      }
      if (data.fields?.length) {
        data.fields.forEach((field) => {
          if (exportFields.value[field]) {
            exportFields.value[field].selected = true
            selectedFieldsOrder.value.push(field)
          }
        })
      }
      showTemplatesModal.value = false
      showCustomizeModal.value = true
    }

    watch(showTemplatesModal, (open) => {
      if (open) {
        fetchOrgFundraiserStatsTemplates()
      }
    })

    function goToCampaign(id) {
      router.push({ name: 'CampaignData', params: { id } })
    }

    return {
      exportData,
      handleDateChange,
      selectedDate,
      selectedExportCampaignFilter,
      selectedExportFundraiserFilter,
      selectedExportHomeOfficeFilter,
      selectedCustomizeCampaignFilter,
      selectedCustomizeFundraiserFilter,
      campaignMultiselectOptions,
      fundraiserFilterOptions,
      homeOfficeFilterOptions,
      campaignOptionLabel,
      showCustomizeModal,
      showSaveTemplateModal,
      showTemplatesModal,
      templateName,
      loadingTemplates,
      savedOrgFundraiserStatsTemplates,
      exportFields,
      selectedFieldsOrder,
      toggleExportField,
      clearExportSelection,
      exportCustomOrgFundraiserStats,
      submitOrgStatsExportTemplate,
      applyOrgFundraiserStatsTemplate,
      removeOrgStatsExportTemplate,
      campaignTableColumns,
      showFilterDrawer,
      activeFilterCount,
      clearExportFilters,
      ArrowDownTrayIcon,
      Cog6ToothIcon,
      DocumentDuplicateIcon,
      FunnelIcon,
      goToCampaign,
    }
  },
}
</script>

<style scoped>
.export-filter-fields :deep(.flatpickr-input) {
  width: 100%;
}

.export-filter-fields :deep(.multiselect) {
  min-height: 0;
}

.export-filter-fields :deep(.multiselect__tags) {
  @apply min-h-[2.125rem] rounded-lg border-0 py-2 pl-3 pr-9 shadow-sm ring-1 ring-inset ring-gray-300;
  gap: 0.125rem;
}

.export-filter-fields :deep(.multiselect__select) {
  height: 2.125rem;
  width: 1.75rem;
}

.export-filter-fields :deep(.multiselect__placeholder),
.export-filter-fields :deep(.multiselect__input),
.export-filter-fields :deep(.multiselect__single) {
  line-height: 1.25rem;
  margin-bottom: 0;
}

.export-filter-fields :deep(.multiselect--active:not(.multiselect--above) .multiselect__tags),
.export-filter-fields :deep(.multiselect--active.multiselect--above .multiselect__tags) {
  @apply ring-2 ring-inset ring-primary;
  border: 0;
  box-shadow: none;
}
</style>
